import React, { useCallback, useState } from 'react';

import { FullScreenModal } from '@opendoor/bricks/complex/Modal';
import { Table, TableBody, TableCell, TableRow } from '@opendoor/bricks/core';

import { useLwcDetailsForBrokerage } from '../../graphql/athena/useLwcDetailsForBrokerage';
import { buildLwcProductData, SingleLead } from '../../helpers/getAllLeadData';
import { LeadDetailsPage } from './LeadDetailsPage';
import LwcDetailsTableRow from './LwcLeadsTableRow';

interface LwcLeadsTableProps {
  agentCustomerUuid: string;
}

const LwcLeadsTable: React.FC<LwcLeadsTableProps> = ({ agentCustomerUuid }) => {
  const [isOpen, setOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<SingleLead | undefined>(undefined);

  const {
    loading,
    error,
    data,
    details: lwcDetailsFromServer,
  } = useLwcDetailsForBrokerage(agentCustomerUuid);

  const handleClose = () => {
    setOpen(false);
  };

  const handleRowClick = useCallback((row: SingleLead) => {
    setSelectedLead(row);
    setOpen(true);
  }, []);

  if (loading || !data) return <p>Loading...</p>;

  if (error) return <p>Error :(</p>;

  let mergedSingleLead: SingleLead | undefined;
  // Uses the latest LWC details from the server after a refetch to for content in the modal
  const selectedLwcDetailFromServer = lwcDetailsFromServer.find(
    (lwcDetails) => lwcDetails.lwcProduct?.sellerLeadToken === selectedLead?.lwcProductData.token,
  );
  if (selectedLwcDetailFromServer && selectedLead) {
    const lwcProductdata = buildLwcProductData(selectedLwcDetailFromServer);
    mergedSingleLead = {
      ...selectedLead,
      lwcProductData: lwcProductdata,
    };
  }

  const rows = lwcDetailsFromServer.map((lwcDetails) => {
    return (
      <LwcDetailsTableRow
        key={lwcDetails.lwcProduct?.sellerLeadToken}
        lwcDetails={lwcDetails}
        agentCustomerUuid={agentCustomerUuid}
        handleClick={handleRowClick}
      />
    );
  });

  return (
    <>
      <Table width="100%" border="1px solid" style={{ borderCollapse: 'collapse' }}>
        <TableBody>
          <TableRow fontWeight="bold" paddingTop={2} paddingBottom={2}>
            <TableCell textAlign="left" fontWeight="bold">
              Address
            </TableCell>
            <TableCell textAlign="left" fontWeight="bold">
              Customer name
            </TableCell>
            <TableCell textAlign="left" fontWeight="bold">
              State
            </TableCell>
            <TableCell textAlign="left" fontWeight="bold">
              Agent
            </TableCell>
            <TableCell textAlign="left" fontWeight="bold">
              ORVA date
            </TableCell>
            <TableCell textAlign="left" fontWeight="bold">
              Offer Expiry
            </TableCell>
          </TableRow>
          {rows}
        </TableBody>
      </Table>
      <FullScreenModal
        isOpen={isOpen && mergedSingleLead !== undefined}
        analyticsName="lwc-details-modal"
        title={'Lead Details'}
        onRequestClose={handleClose}
      >
        {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
        <LeadDetailsPage leadDetails={mergedSingleLead!} brokerageView={true} />
      </FullScreenModal>
    </>
  );
};

export default LwcLeadsTable;
